/** The click-through fields svc-bedbank needs to honour Google's advertised price (GHA-553). */
export interface GhaPriceMatchParams {
  token: string
  roomId: string
  refundable: boolean
  /** Google's displayed tax and fees. Both or neither: svc-bedbank ignores one on its own. */
  sourceTax?: number
  sourceFees?: number
}

export interface GhaPriceMatchClickThrough extends GhaPriceMatchParams {
  propertyId: string
  /** GHA account the landing URL names (lpp, scoopon, ...); the rollout is decided per account. */
  accountType: string
}

/** Everything svc-connection-google's eligibility verdict depends on. */
export function ghaPriceMatchKey(
  { accountType, propertyId, token }: Pick<GhaPriceMatchClickThrough, 'accountType' | 'propertyId' | 'token'>,
): string {
  return `${accountType}|${propertyId}|${token}`
}

/**
 * The only fields svc-bedbank accepts inside `gha`: it rejects an enquiry carrying anything else.
 * Callers hold the wider match context, so every boundary that stores or sends it picks here.
 */
export function pickGhaParams(gha: GhaPriceMatchParams): GhaPriceMatchParams {
  return { token: gha.token, roomId: gha.roomId, refundable: gha.refundable, sourceTax: gha.sourceTax, sourceFees: gha.sourceFees }
}

export function ghaQueryParams(gha: GhaPriceMatchParams | undefined) {
  return gha && {
    ghaToken: gha.token,
    ghaRoomId: gha.roomId,
    ghaRefundable: gha.refundable,
    // svc-accommodation's contract types these query params as strings.
    ghaSourceTax: gha.sourceTax?.toString(),
    ghaSourceFees: gha.sourceFees?.toString(),
  }
}

// svc-connection-google writes Google's PackageID as bf?_dn?_int?_park?_ref?_S<supplier>_pkg;
// the ref digit is the refundability of the advertised rate.
const GHA_PACKAGE_REFUNDABLE_PATTERN = /_ref([01])_/

export function parseGhaPackageRefundable(roomRateId: string | undefined): boolean | undefined {
  const digit = roomRateId ? GHA_PACKAGE_REFUNDABLE_PATTERN.exec(roomRateId)?.[1] : undefined
  if (digit === undefined) return undefined
  return digit === '1'
}

export function parseGhaRefundableParam(value: string | undefined): boolean | undefined {
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

export function parseGhaAmountParam(value: string | undefined): number | undefined {
  // An empty param is Google telling us nothing, not telling us zero — `Number('')` would read it
  // as a legitimate 0.
  if (!value?.trim()) return undefined
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : undefined
}
